import { SUPPORT_HREF } from "../lib/support";
import { useTranslation } from "../i18n/useTranslation";
import { useOptionalConsent } from "../analytics/ConsentContext";
import { ConsentFooterItems, ConsentStatus } from "./Consent";
import { SessionRow } from "./SessionRow";
import { LanguageSwitch } from "./LanguageSwitch";
import { NewTabLink } from "./NewTabLink";
import { FooterRecaptchaNotice } from "../../../../shared/ui/RecaptchaNotice";
import { PRIVACY_POLICY_URL } from "../features/terms/terms";
import { useTermsDocumentUrl } from "../features/terms/TermsDocumentContext";

// The footer Product specified for book.*: publisher credit plus the legal and
// support destinations.
//
// ONE LEGAL GROUP, not two. Product resolved the duplicate-link question after
// the consent surface landed: the footer showed a marketing "Privacy" link and
// a second "Privacy notice" link immediately below it, which is two answers to
// the same question stacked on top of each other.
//
// So the marketing Privacy item IS the Privacy notice item now, it uses the
// Legal-approved notice target, and Cookie settings sits beside it. Terms and
// Support stay as they were.
//
// ONE PRIVACY LINK, WHICHEVER APPLIES (Web Director, 2026-09-27). Analytics
// is off in every environment, so the published policy at
// uselura.com/privacy/ covers this surface and the footer links it beside
// Terms. When a Legal-approved analytics notice is configured, the consent
// surface becomes askable and `ConsentFooterItems` renders ITS Privacy link;
// this one then steps aside, keyed on the same `askable` flag, so the two can
// never render together.
//
// The destinations belong to the MARKETING SITE and are linked, never
// duplicated. Product was explicit that Privacy and Terms need legal review and
// that Web must not invent that language — so there is no local copy of it here
// and there must not be one.
//
// Minimal by instruction: this is not marketing navigation, and it must not
// grow into it.
//
// NO EXPLICIT ROLE. A `<footer>` is the page's contentinfo landmark only where
// it is the PAGE's footer (a sibling of <main>, as on PageShell); placed inside
// <main> — the profile's and the outcome's scrolling content, which v3 draws
// above a pinned action bar — it is that section's footer and no landmark.
// Forcing `role="contentinfo"` made every one of those a contentinfo nested in
// <main> (axe landmark-contentinfo-is-top-level). The native mapping is the
// right one in both places.
export default function Footer({ as: Root = "footer" }: { as?: "footer" | "div" } = {}) {
  const { t, locale } = useTranslation();
  const consentPrivacyShown = useOptionalConsent()?.askable === true;
  const termsUrl = useTermsDocumentUrl();

  return (
    <Root className="b-footer">
      {/* On the page rather than inside the dialog, so it survives the dialog
          closing and is still there to be read. Renders only for the
          interaction that produced it — see ConsentStatus. */}
      <ConsentStatus />
      <div className="v3-footer-meta">
      {/* Whose session this is, with Sign out: above the language and legal
          row, only for a signed-in client (SessionRow.tsx). */}
      <SessionRow />
      <div className="v3-legal-row">
      <LanguageSwitch />
      <nav aria-label={t("footer.navLabel")}>
        <ConsentFooterItems />
        {consentPrivacyShown ? null : (
          <NewTabLink href={PRIVACY_POLICY_URL} testId="footer-privacy">
            {t("footer.privacy")}
          </NewTabLink>
        )}
        {/* The Terms the page read (Review, and the outcome that replaces
            it), else the published page at uselura.com/terms/, written with
            its trailing slash like the Privacy link beside it. */}
        <NewTabLink href={termsUrl} testId="footer-terms">
          {t("footer.terms")}
        </NewTabLink>
        <a href={SUPPORT_HREF}>{t("footer.support")}</a>
      </nav>
      </div>
      {/* Google's reCAPTCHA disclosure, on every page: here, unless the screen's
          protected action carries it (one per screen). */}
      <FooterRecaptchaNotice locale={locale} />
      <p className="v3-publisher">Lura · Kindfield Works LLC</p></div>
    </Root>
  );
}
